/**
 * plan_read — Read a Plan entity's content and parse its structured state.
 *
 * Returns:
 * - content: full markdown text (assembled from blocks)
 * - state: parsed YAML from the first page-code[language=yaml] block
 * - checklist: array of { text, checked } from page-todo blocks
 * - status: extracted from the entity skeleton
 * - planId: the entity ID
 * - nodes: raw block nodes (for debugging / detailed inspection)
 */

import { SimpleDocCommand } from '../DocsApp/commands/v5/types';
import { StandardToolResult, createSuccessResult, createErrorResult } from '../../types/standardToolResults';
import { CommandExecutionContext } from '../../types/commands';
import { getApplicationForEntity } from '../../services/applicationRegistry';

function parseYamlState(yamlText: string): Record<string, any> {
  try {
    // Simple key: value YAML parser (handles nested via indent)
    // For production quality we'd use js-yaml, but we keep this dependency-free
    const lines = yamlText.split('\n');
    const result: Record<string, any> = {};
    const stack: Array<{ obj: Record<string, any>; indent: number }> = [{ obj: result, indent: -1 }];

    for (const line of lines) {
      if (!line.trim() || line.trim().startsWith('#')) continue;
      const indent = line.length - line.trimStart().length;
      const colonIdx = line.indexOf(':');
      if (colonIdx === -1) continue;

      const key = line.slice(0, colonIdx).trim();
      const rawValue = line.slice(colonIdx + 1).trim();

      // Pop stack to correct depth
      while (stack.length > 1 && stack[stack.length - 1].indent >= indent) {
        stack.pop();
      }

      const parent = stack[stack.length - 1].obj;

      if (rawValue === '' || rawValue === null) {
        // Nested object coming
        const nested: Record<string, any> = {};
        parent[key] = nested;
        stack.push({ obj: nested, indent });
      } else {
        // Scalar value
        let value: any = rawValue;
        if (rawValue === 'true') value = true;
        else if (rawValue === 'false') value = false;
        else if (rawValue === 'null') value = null;
        else if (!isNaN(Number(rawValue)) && rawValue !== '') value = Number(rawValue);
        // Strip inline comments
        else if (typeof value === 'string') {
          const commentIdx = value.indexOf('  #');
          if (commentIdx !== -1) value = value.slice(0, commentIdx).trim();
        }
        parent[key] = value;
      }
    }
    return result;
  } catch {
    return {};
  }
}

function blocksToMarkdown(nodes: any[]): string {
  return nodes
    .map((n: any) => {
      const text = n.attrs?.text || n.text || '';
      switch (n.className) {
        case 'page-heading-1': return `# ${text}`;
        case 'page-heading-2': return `## ${text}`;
        case 'page-heading-3': return `### ${text}`;
        case 'page-paragraph': return text;
        case 'page-bullet': return `- ${text}`;
        case 'page-numbered': return `1. ${text}`;
        case 'page-todo': return `- [${n.attrs?.checked ? 'x' : ' '}] ${text}`;
        case 'page-code': return `\`\`\`${n.attrs?.language || ''}\n${text}\n\`\`\``;
        case 'page-callout': return `> ${text}`;
        case 'page-divider': return '---';
        default: return text;
      }
    })
    .join('\n');
}

export const plan_read: SimpleDocCommand = {
  id: 'plan_read',
  logicalName: 'plan_read',
  version: 'v1',

  description: `Read a Plan entity and return its structured state.

Returns the full markdown content, the parsed YAML state block, and the checklist.
Use this at the start of each turn to know where the workflow is.

**IMPORTANT:** Always call plan_read before acting so you know the current step.

**OUTPUT:**
{
  planId: "plan_abc",
  status: "not_started | in_progress | complete",
  content: "full markdown",
  state: { workflow: { status, current_step, ... }, ... },  // parsed YAML
  checklist: [{ nodeId, text, checked }],
  nodes: [...]
}`,

  schema: {
    parameters: [
      {
        name: 'entityId',
        type: 'string',
        description: 'Plan entity ID (plan_*)',
        required: true,
      },
    ],
  },

  examples: {
    good: [{ input: { entityId: 'plan_cos_setup' }, expectedOutput: createSuccessResult('Read plan', {}), description: 'Read the COS setup plan' }],
    bad: [],
  },

  handler: async (params: any, _context: CommandExecutionContext): Promise<StandardToolResult> => {
    const { entityId } = params;
    if (!entityId) return createErrorResult('Missing entityId', 'entityId is required', 'validation');

    try {
      const app = await getApplicationForEntity(entityId);
      if (!app) {
        return createErrorResult('Plan not found', `No application for ${entityId}. Ensure the entity exists.`, 'validation');
      }

      const result = await app.handleEntityRead({ all: true }, {});
      const nodes: any[] = result.nodes ?? [];

      // Extract YAML state block (first page-code[language=yaml])
      const yamlBlock = nodes.find((n: any) => n.className === 'page-code' && n.attrs?.language === 'yaml');
      const state = yamlBlock ? parseYamlState(yamlBlock.attrs?.text || '') : {};

      // Extract checklist items
      const checklist = nodes
        .filter((n: any) => n.className === 'page-todo')
        .map((n: any) => ({ nodeId: n.id, text: n.attrs?.text || n.text || '', checked: !!n.attrs?.checked }));

      // Get status from entity store skeleton (faster than re-parsing YAML)
      let skeletonStatus = 'not_started';
      try {
        const entityStore = (await import('../../stores/entityStore')).default.getState();
        const entity = entityStore.entities.find(
          (e: any) => e._id === entityId || e.llmId === entityId
        );
        skeletonStatus = entity?.skeleton?.status || (state as any)?.workflow?.status || 'not_started';
      } catch { /* non-fatal */ }

      const markdown = blocksToMarkdown(nodes);

      return createSuccessResult(`Read plan: ${entityId}`, {
        planId: entityId,
        status: skeletonStatus,
        content: markdown,
        state,
        checklist,
        nodeCount: nodes.length,
      });
    } catch (error: any) {
      return createErrorResult('Failed to read plan', error.message || 'Unknown error', 'execution');
    }
  },
};
